import { useState, useEffect, useRef } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';

// Rol sabitleri
export const ROLES = {
  ADMIN: 'admin',
  SATISCI: 'satisci',
  MONTAJCI: 'montajci',
  YAZILIMCI: 'yazilimci'
};

// Rol yetkileri
export const PERMISSIONS = {
  [ROLES.ADMIN]: {
    canAccessDashboard: true,
    canAccessCustomers: true,
    canAccessProducts: true,
    canManageUsers: true,
    canSeeAllData: true,
    canCreateCustomer: true,
    canEditCustomer: true,
    canDeleteCustomer: true,
    canCreateOrder: true,
    canEditOrder: true,
    canDeleteOrder: true,
    canUpdateOrderStatus: true,
  },
  [ROLES.SATISCI]: {
    canAccessDashboard: false,
    canAccessCustomers: true,
    canAccessProducts: true,
    canManageUsers: false,
    canSeeAllData: true,
    canCreateCustomer: true,
    canEditCustomer: true,
    canDeleteCustomer: true,
    canCreateOrder: true,
    canEditOrder: true,
    canDeleteOrder: true,
    canUpdateOrderStatus: true,
  },
  [ROLES.MONTAJCI]: {
    canAccessDashboard: false,
    canAccessCustomers: true, // Sadece montaj durumundakiler
    canAccessProducts: false,
    canManageUsers: false,
    canSeeAllData: false, // Sadece montaj durumundakiler
    canCreateCustomer: false,
    canEditCustomer: false,
    canDeleteCustomer: false,
    canCreateOrder: false,
    canEditOrder: false,
    canDeleteOrder: false,
    canUpdateOrderStatus: true, // Sadece teslim edildi yapabilir
    onlyMontajOrders: true,
  },
  [ROLES.YAZILIMCI]: {
    canAccessDashboard: true,
    canAccessCustomers: true,
    canAccessProducts: true,
    canManageUsers: false,
    canSeeAllData: false, // Sadece kendi verileri
    canCreateCustomer: true,
    canEditCustomer: true,
    canDeleteCustomer: true,
    canCreateOrder: true,
    canEditOrder: true,
    canDeleteOrder: true,
    canUpdateOrderStatus: true,
    onlyOwnData: true,
  },
};

export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  // Şifre değişikliği/token yenilemesi sırasında mevcut UID'yi takip et.
  // Aynı kullanıcı olduğunda user state'i null'a düşmez → Dashboard unmount olmaz.
  const currentUidRef = useRef(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const isSameUser = currentUidRef.current === firebaseUser.uid;
        currentUidRef.current = firebaseUser.uid;

        // Her durumda user objesini güncelle (token yenileme durumunda bile)
        setUser(firebaseUser);

        // Aynı kullanıcı ise loading'i tekrar true'ya alma (Dashboard unmount etme)
        // Farklı kullanıcı ise loading zaten true'dur (initial state)

        try {
          const userDocRef = doc(db, 'users', firebaseUser.uid);
          const userDoc = await getDoc(userDocRef);

          if (userDoc.exists()) {
            const userData = userDoc.data();

            // Kullanıcı disabled ise çıkış yap
            if (userData.disabled === true) {
              currentUidRef.current = null;
              await signOut(auth);
              setUser(null);
              setUserRole(null);
              setPermissions(null);
              setLoading(false);
              return;
            }

            const role = userData.role || ROLES.SATISCI;
            setUserRole(role);
            setPermissions(PERMISSIONS[role] || PERMISSIONS[ROLES.SATISCI]);
          } else {
            // Users collection'da kayıt yoksa Admin olarak oluştur
            const defaultRole = ROLES.ADMIN;
            await setDoc(userDocRef, {
              email: firebaseUser.email,
              role: defaultRole,
              displayName: firebaseUser.displayName || firebaseUser.email.split('@')[0],
              createdAt: new Date(),
              createdBy: 'system',
            });
            setUserRole(defaultRole);
            setPermissions(PERMISSIONS[defaultRole]);
          }
        } catch (error) {
          console.error('Kullanıcı rolü alınamadı:', error);
          // Aynı kullanıcıysa mevcut rolü koru; yeni kullanıcıysa varsayılan ata
          if (!isSameUser) {
            setUserRole(ROLES.SATISCI);
            setPermissions(PERMISSIONS[ROLES.SATISCI]);
          }
        }
      } else {
        // Gerçek çıkış
        currentUidRef.current = null;
        setUser(null);
        setUserRole(null);
        setPermissions(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Yetki kontrol fonksiyonu
  const hasPermission = (permission) => {
    return permissions?.[permission] || false;
  };

  // Rol kontrol fonksiyonu
  const isRole = (role) => {
    return userRole === role;
  };

  return { 
    user, 
    userRole, 
    permissions, 
    loading, 
    hasPermission,
    isRole,
    isAdmin: userRole === ROLES.ADMIN,
    isSatisci: userRole === ROLES.SATISCI,
    isMontajci: userRole === ROLES.MONTAJCI,
    isYazilimci: userRole === ROLES.YAZILIMCI,
  };
};
